import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { 
  Play,
  Pause,
  Volume2,
  VolumeX,
  Maximize,
  Keyboard,
  MessageSquare,
  LogIn,
  CreditCard,
  ChevronRight,
  ArrowUpRight,
  BookOpen,
  Zap,
  Target,
  HelpCircle
} from 'lucide-react';
import UserNavbar from '../components/UserNavbar';
import Footer from '../components/Footer';
import config from '../../config/env';

const VideoPlayer = ({ src, poster, title, id }) => {
  const [isPlaying, setIsPlaying] = useState(false);
  const [isMuted, setIsMuted] = useState(false);
  const [showPlaceholder, setShowPlaceholder] = useState(!src);

  const handlePlayPause = () => {
    const video = document.getElementById(id);
    if (video) {
      if (isPlaying) {
        video.pause();
      } else {
        video.play();
      }
      setIsPlaying(!isPlaying);
    }
  };

  const handleMute = () => {
    const video = document.getElementById(id);
    if (video) {
      video.muted = !isMuted;
      setIsMuted(!isMuted);
    }
  };

  const handleFullscreen = () => {
    const video = document.getElementById(id);
    if (video) {
      if (video.requestFullscreen) {
        video.requestFullscreen();
      } else if (video.webkitRequestFullscreen) {
        video.webkitRequestFullscreen();
      }
    }
  };

  if (showPlaceholder || !src) {
    return (
      <div className="relative w-full aspect-[16/10] bg-[#0a0a0a] border border-white/10 rounded-lg overflow-hidden group">
        {/* Placeholder Content */}
        <div className="absolute inset-0 flex flex-col items-center justify-center">
          <div className="w-20 h-20 rounded-full border-2 border-white/20 flex items-center justify-center mb-6 group-hover:border-accent/50 transition-colors">
            <Play className="h-8 w-8 text-white/40 group-hover:text-accent transition-colors ml-1" />
          </div>
          <p className="text-white/40 text-sm font-mono tracking-wide">{title || 'VIDEO COMING SOON'}</p>
          <p className="text-white/20 text-xs mt-2">Tutorial video will be available here</p>
        </div>
        
        {/* Grid Pattern Background */}
        <div 
          className="absolute inset-0 opacity-5"
          style={{
            backgroundImage: 'linear-gradient(rgba(255,255,255,0.1) 1px, transparent 1px), linear-gradient(90deg, rgba(255,255,255,0.1) 1px, transparent 1px)',
            backgroundSize: '50px 50px'
          }}
        />
      </div>
    );
  }

  return (
    <div className="relative w-full aspect-[16/10] bg-[#0a0a0a] border border-white/10 rounded-lg overflow-hidden group">
      <video
        id={id}
        className="w-full h-full object-contain bg-black"
        poster={poster}
        onEnded={() => setIsPlaying(false)}
      >
        <source src={src} type="video/mp4" />
        Your browser does not support the video tag.
      </video>

      {/* Video Controls Overlay */}
      <div className="absolute bottom-0 left-0 right-0 p-4 bg-gradient-to-t from-black/80 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-4">
            <button 
              onClick={handlePlayPause}
              className="p-2 hover:bg-white/10 rounded-full transition-colors"
            >
              {isPlaying ? (
                <Pause className="h-5 w-5 text-white" />
              ) : (
                <Play className="h-5 w-5 text-white ml-0.5" />
              )}
            </button>
            <button 
              onClick={handleMute}
              className="p-2 hover:bg-white/10 rounded-full transition-colors"
            >
              {isMuted ? (
                <VolumeX className="h-5 w-5 text-white" />
              ) : (
                <Volume2 className="h-5 w-5 text-white" />
              )}
            </button>
          </div>
          <button 
            onClick={handleFullscreen}
            className="p-2 hover:bg-white/10 rounded-full transition-colors"
          >
            <Maximize className="h-5 w-5 text-white" />
          </button>
        </div>
      </div>

      {/* Play Button Overlay (when paused) */}
      {!isPlaying && (
        <div 
          className="absolute inset-0 flex items-center justify-center cursor-pointer"
          onClick={handlePlayPause}
        >
          <div className="w-20 h-20 rounded-full bg-white/10 backdrop-blur-sm flex items-center justify-center hover:bg-white/20 transition-colors">
            <Play className="h-8 w-8 text-white ml-1" />
          </div>
        </div>
      )}
    </div>
  );
};

// Guide Video Section with Toggle
const GuideVideoSection = () => {
  const [activeTab, setActiveTab] = useState('mcq');

  return (
    <div>
      {/* Toggle Buttons */}
      <div className="flex gap-2 mb-6">
        <button
          onClick={() => setActiveTab('mcq')}
          className={`px-4 py-2 text-sm font-mono tracking-wide border transition-all duration-200 ${
            activeTab === 'mcq'
              ? 'bg-white text-black border-white'
              : 'bg-transparent text-white/60 border-white/20 hover:border-white/40 hover:text-white'
          }`}
        >
          MCQ GUIDE
        </button>
        <button
          onClick={() => setActiveTab('coding')}
          className={`px-4 py-2 text-sm font-mono tracking-wide border transition-all duration-200 ${
            activeTab === 'coding'
              ? 'bg-white text-black border-white'
              : 'bg-transparent text-white/60 border-white/20 hover:border-white/40 hover:text-white'
          }`}
        >
          CODING GUIDE
        </button>
      </div>

      {/* Video Player */}
      <div className="relative">
        {activeTab === 'mcq' && (
          <VideoPlayer 
            src={config.videos.guideMcq}
            title="MCQ TUTORIAL"
            id="guide-video-mcq"
          />
        )}
        {activeTab === 'coding' && (
          <VideoPlayer 
            src={config.videos.guideCoding}
            title="CODING TUTORIAL"
            id="guide-video-coding"
          />
        )}
      </div>

      {/* Video Description */}
      <p className="text-white/40 text-sm mt-4">
        {activeTab === 'mcq' 
          ? 'Learn how to use keyboard shortcuts for solving MCQ questions efficiently.'
          : 'Learn how to use the AI-powered coding assistant for programming problems.'
        }
      </p>
    </div>
  );
};

const Guide = () => {
  const usageSteps = [
    {
      icon: LogIn,
      num: '01',
      title: 'Login to Extension',
      description: 'Open the NeoPass extension popup and login with your account credentials.',
    },
    {
      icon: BookOpen,
      num: '02',
      title: 'Navigate to Study Material',
      description: 'Open the learning platform or content you want to study.',
    },
    {
      icon: Keyboard,
      num: '03',
      title: 'Use Keyboard Shortcuts',
      description: 'Press the configured shortcuts to activate the AI learning assistant.',
    },
    {
      icon: MessageSquare,
      num: '04',
      title: 'Ask Questions',
      description: 'Type your questions and get instant AI-powered explanations.',
    },
    {
      icon: Target,
      num: '05',
      title: 'Practice & Learn',
      description: 'Use the assistant to clarify concepts and prepare for exams.',
    },
    {
      icon: CreditCard,
      num: '06',
      title: 'Manage Credits',
      description: 'Monitor your credit usage from the dashboard and purchase more when needed.',
    },
  ];

  const features = [
    {
      icon: Zap,
      title: 'Instant Answers',
      description: 'Get immediate explanations for complex topics without searching through multiple sources.',
    },
    {
      icon: BookOpen,
      title: 'Contextual Learning',
      description: 'The AI understands the context of your study material for relevant explanations.',
    },
    {
      icon: Target,
      title: 'Exam Focus',
      description: 'Prepare effectively with explanations tailored for assessment success.',
    },
  ];

  return (
    <div className="min-h-screen bg-[#111111]">
      <UserNavbar />

      {/* Hero Section */}
      <section className="pt-32 pb-16 px-6 md:px-12 lg:px-20">
        <div className="max-w-7xl mx-auto">
          <span className="text-white/40 text-xs font-mono tracking-widest mb-4 block">LEARN</span>
          <h1 className="text-4xl md:text-5xl lg:text-6xl font-semibold text-white mb-6 tracking-tight">
            Usage Guide
          </h1>
          <p className="text-white/60 text-lg max-w-2xl">
            Learn how to use NeoPass effectively and make the most of your AI-powered learning assistant.
          </p>
        </div>
      </section>

      {/* Main Video Tutorial Section */}
      <section className="py-12 px-6 md:px-12 lg:px-20">
        <div className="max-w-7xl mx-auto">
          <div className="flex items-center gap-3 mb-6">
            <span className="text-white/40 text-xs font-mono tracking-widest">GETTING STARTED</span>
          </div>
          
          <div className="grid lg:grid-cols-2 gap-8 items-start">
            <div>
              <h2 className="text-2xl md:text-3xl font-light text-white mb-4">
                Quick start tutorial
              </h2>
              <p className="text-white/50 mb-6">
                This video covers everything you need to know to start using NeoPass. From logging in to using keyboard shortcuts effectively.
              </p>
              
              {/* Quick Tips */}
              <div className="space-y-3 mb-8">
                {features.map((feature, index) => (
                  <div 
                    key={index}
                    className="flex items-start gap-3 p-4 bg-white/[0.02] border border-white/10 rounded-lg"
                  >
                    <feature.icon className="h-5 w-5 text-accent mt-0.5" />
                    <div>
                      <h4 className="text-white text-sm font-medium">{feature.title}</h4>
                      <p className="text-white/50 text-xs mt-1">{feature.description}</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* Video Player with Toggle */}
            <GuideVideoSection />
          </div>
        </div>
      </section>

      {/* Keyboard Shortcuts Section */}
      <section className="py-12 px-6 md:px-12 lg:px-20 bg-[#0a0a0a]">
        <div className="max-w-7xl mx-auto">
          <div className="grid lg:grid-cols-2 gap-16 items-start">
            <div>
              <div className="flex items-center gap-3 mb-4">
                <span className="text-white/40 text-xs font-mono tracking-widest">POWER USER</span>
              </div>
              <h2 className="text-2xl md:text-3xl font-light text-white mb-6">
                Keyboard shortcuts
              </h2>
              <p className="text-white/50 mb-8">
                Master these shortcuts to navigate NeoPass efficiently without interrupting your study flow.
              </p>

              {/* Video for shortcuts */}
              <VideoPlayer 
                src={config.videos.guideMcq}
                poster=""
                title="SHORTCUTS DEMO"
                id="guide-video-shortcuts"
              />
            </div>

            <div className="space-y-px">
              {config.shortcuts.map((shortcut, index) => (
                <div 
                  key={index} 
                  className="flex items-center justify-between p-5 bg-white/[0.02] border border-white/10 hover:border-white/20 transition-colors group"
                >
                  <div className="flex items-center gap-2">
                    {shortcut.keys.map((key, i) => (
                      <React.Fragment key={i}>
                        <kbd className="px-3 py-1.5 text-xs font-mono bg-white/5 text-white/70 border border-white/10 group-hover:border-white/20 transition-colors">
                          {key}
                        </kbd>
                        {i < shortcut.keys.length - 1 && (
                          <span className="text-white/20">+</span>
                        )}
                      </React.Fragment>
                    ))}
                  </div>
                  <span className="text-white/60 text-sm font-medium">
                    {shortcut.action}
                  </span>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Step by Step Usage */}
      <section className="py-12 px-6 md:px-12 lg:px-20">
        <div className="max-w-7xl mx-auto">
          <div className="flex items-center gap-3 mb-6">
            <span className="text-white/40 text-xs font-mono tracking-widest">WORKFLOW</span>
          </div>
          <h2 className="text-2xl md:text-3xl font-light text-white mb-10">
            How to use NeoPass
          </h2>
          
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-px bg-white/10">
            {usageSteps.map((step, index) => (
              <div 
                key={index}
                className="bg-[#111111] p-8 group hover:bg-white/[0.02] transition-all duration-500"
              >
                <div className="flex items-center justify-between mb-4">
                  <span className="text-3xl font-light text-white/20 group-hover:text-accent/50 transition-colors font-mono">
                    {step.num}
                  </span>
                  <div className="p-2 border border-white/10 group-hover:border-accent/30 transition-colors">
                    <step.icon className="h-5 w-5 text-white/40 group-hover:text-accent transition-colors" />
                  </div>
                </div>
                <h3 className="text-lg font-medium text-white mb-2 group-hover:text-accent transition-colors">
                  {step.title}
                </h3>
                <p className="text-white/50 text-sm leading-relaxed">
                  {step.description}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* FAQ Section */}
      <section className="py-12 px-6 md:px-12 lg:px-20 bg-[#0a0a0a]">
        <div className="max-w-7xl mx-auto">
          <div className="flex items-center gap-3 mb-6">
            <span className="text-white/40 text-xs font-mono tracking-widest">FAQ</span>
          </div>
          <h2 className="text-2xl md:text-3xl font-light text-white mb-6">
            Common questions
          </h2>
          
          <div className="space-y-4">
            <div className="p-6 bg-white/[0.02] border border-white/10 hover:border-white/20 transition-colors">
              <div className="flex items-start gap-4">
                <HelpCircle className="h-5 w-5 text-accent mt-0.5" />
                <div>
                  <h4 className="text-white font-medium mb-2">How do credits work?</h4>
                  <p className="text-white/50 text-sm">Each AI interaction consumes credits based on the complexity of your query. You can monitor your balance on the dashboard and purchase more credits anytime.</p>
                </div>
              </div>
            </div>
            <div className="p-6 bg-white/[0.02] border border-white/10 hover:border-white/20 transition-colors">
              <div className="flex items-start gap-4">
                <HelpCircle className="h-5 w-5 text-accent mt-0.5" />
                <div>
                  <h4 className="text-white font-medium mb-2">Can I use this on any website?</h4>
                  <p className="text-white/50 text-sm">NeoPass is optimized for specific learning platforms. Check the supported platforms list in your dashboard for the best experience.</p>
                </div>
              </div>
            </div>
            <div className="p-6 bg-white/[0.02] border border-white/10 hover:border-white/20 transition-colors">
              <div className="flex items-start gap-4">
                <HelpCircle className="h-5 w-5 text-accent mt-0.5" />
                <div>
                  <h4 className="text-white font-medium mb-2">What if the shortcuts don't work?</h4>
                  <p className="text-white/50 text-sm">Make sure you're on a supported page and the extension is active. Some websites may have conflicting shortcuts - try refreshing the page or checking extension permissions.</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-16 px-6 md:px-12 lg:px-20">
        <div className="max-w-7xl mx-auto">
          <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-6">
            <div>
              <h2 className="text-2xl md:text-3xl font-light text-white mb-2">
                Need more help?
              </h2>
              <p className="text-white/50">
                Check our detailed documentation or contact support for assistance.
              </p>
            </div>
            <div className="flex flex-wrap gap-4">
              <Link 
                to="/must-read"
                className="inline-flex items-center gap-2 px-6 py-3 bg-white text-black font-medium hover:bg-white/90 transition-colors"
              >
                View Documentation
                <ChevronRight className="h-4 w-4" />
              </Link>
              <Link 
                to="/contact"
                className="inline-flex items-center gap-2 px-6 py-3 border border-white/20 text-white font-medium hover:bg-white/5 transition-colors"
              >
                Contact Support
                <ArrowUpRight className="h-4 w-4" />
              </Link>
            </div>
          </div>
        </div>
      </section>

      <Footer variant="full" />
    </div>
  );
};

export default Guide;
